import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, Bell, ChevronDown,
  LayoutDashboard, GitBranch, BarChart3, UserCog,
  Search, Filter, Star, Users, Calendar, ArrowDownToLine, MessageCircle,
  PanelLeft, ChevronLeft, ChevronRight, User, Award, Percent, Menu,
} from 'lucide-react';
import { api, ApiError, egp, formatBookingDays, type Booking } from '../api';
import { useAuth } from '../auth';
import { ClinicWelcomeTour } from '../components/ClinicWelcomeTour';
import { BrandMark } from '../components/BrandLogo';
import {
  clinicWelcomeIsPending,
  dismissClinicWelcome,
} from '../lib/clinicWelcome';
import { ClinicChatPanel, type ClinicConsultation } from '../components/clinic/ClinicChatPanel';
import { ClinicRatingModal, type EvalScores } from '../components/clinic/ClinicRatingModal';
import { ClinicAnalyticsPanel } from '../components/clinic/ClinicAnalyticsPanel';
import '../styles/clinic-dashboard.css';

type ClinicStats = {
  payoutRatePerWeekEgp: number;
  earnedEgp: number;
  projectedEgp: number;
  completedWeeks: number;
  activeWeeks: number;
  bookedCount: number;
  scheduledCount: number;
  completedCount: number;
  cancelledCount: number;
  uniqueStudents: number;
  evaluationsPending: number;
  certificatesIssued: number;
  certificatesPending: number;
  activeSchedules: number;
  locationsCount: number;
  studentsThisMonth: number;
  completedThisMonth: number;
  earnedThisMonthEgp: number;
  upcomingCount: number;
  inProgressCount: number;
  pastOpenCount: number;
  thisWeekCount: number;
  nextWeekCount: number;
  awaitingAttendance: number;
  awaitingCompletion: number;
  uniqueUpcomingStudents: number;
  uniqueInProgressStudents: number;
  totalSessionsBooked: number;
  reservationsGrowthPct: number | null;
  availableEgp: number;
  pendingWithdrawalEgp: number;
  paidOutEgp: number;
  internWeeks: number;
  totalRevenueEgp: number;
  branches?: Array<{
    id: string;
    name: string;
    city: string;
    isActive: boolean;
    completedWeeks: number;
    earnedEgp: number;
    activeWeeks: number;
    internWeeks: number;
  }>;
};

type ClinicBooking = Booking;

function phaseOf(b: ClinicBooking): NonNullable<ClinicBooking['phase']> {
  return b.phase ?? (b.status === 'cancelled' ? 'cancelled' : 'upcoming');
}

/** Saturday–Friday calendar week, matching clinic stats. */
function thisWeekRange(now = new Date()) {
  const ymd = now.toISOString().slice(0, 10);
  const [year, month, day] = ymd.split('-').map(Number);
  const utc = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1));
  const delta = (utc.getUTCDay() - 6 + 7) % 7;
  utc.setUTCDate(utc.getUTCDate() - delta);
  const start = utc.toISOString().slice(0, 10);
  utc.setUTCDate(utc.getUTCDate() + 6);
  return { start, end: utc.toISOString().slice(0, 10) };
}

function overlapsThisWeek(b: ClinicBooking) {
  if (b.status === 'cancelled') return false;
  const { start, end } = thisWeekRange();
  return b.weekStartDate <= end && b.weekEndDate >= start;
}

function formatSlot(t: string) {
  const [h, m] = t.split(':').map(Number);
  const hour = h ?? 0;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${ampm}` : `${h12} ${ampm}`;
}

function formatSchedule(b: ClinicBooking) {
  const days = (b.dates ?? []).map(value => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year!, month! - 1, day!).toLocaleDateString('en-US', { weekday: 'short' });
  });
  const unique = [...new Set(days)];
  const label = unique.length ? unique.join(', ') : formatBookingDays(b);
  return `${label}  ${formatSlot(b.slotStartTime)}–${formatSlot(b.slotEndTime)}`;
}

function weekProgress(b: ClinicBooking) {
  const days = b.attendanceDays ?? [];
  const total = days.length || b.sessionCount || 0;
  const done = days.filter(d => d.attended).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : (b.weekPercent ?? 0);
  return { done, total, pct };
}

function formatAttendDay(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
  });
}

type InternStatusTone = 'active' | 'pending' | 'rated' | 'upcoming';

function internStatus(b: ClinicBooking): { label: string; tone: InternStatusTone } {
  if (b.hasEvaluation && b.canIssueCertificate) {
    return { label: 'Pending certificate', tone: 'pending' };
  }
  if (b.hasEvaluation) return { label: 'Rated', tone: 'rated' };
  if (b.canEvaluate) return { label: 'Pending rating', tone: 'pending' };
  if (b.canMarkComplete) return { label: 'Pending completion', tone: 'pending' };
  const needsAttendance = (b.attendanceDays ?? []).some(d => !d.attended);
  if (needsAttendance && phaseOf(b) === 'in_progress') {
    return { label: 'Pending attendance', tone: 'pending' };
  }
  if (phaseOf(b) === 'upcoming') return { label: 'Upcoming', tone: 'upcoming' };
  return { label: 'Active', tone: 'active' };
}

function statusPillClass(tone: InternStatusTone) {
  if (tone === 'rated') return 'sp-rated';
  if (tone === 'pending') return 'sp-pending';
  return 'sp-active';
}

function StudentPhoto({
  photoUrl,
  size = 'md',
  blurred = false,
}: {
  photoUrl?: string | null;
  size?: 'sm' | 'md';
  blurred?: boolean;
}) {
  const cls = [
    'cd-student-av',
    size === 'sm' ? 'cd-student-av--sm' : '',
    blurred ? 'cd-student-av--blur' : '',
  ].filter(Boolean).join(' ');
  if (photoUrl) {
    return <img src={photoUrl} alt="" className={cls} />;
  }
  return (
    <div className={`${cls} cd-student-av--fallback`} title="No photo uploaded" aria-hidden>
      <User className="cd-student-av-ico" />
    </div>
  );
}

function InternRowActions({
  b,
  isActioning,
  onAttend,
  onComplete,
  onRate,
  onCert,
}: {
  b: ClinicBooking;
  isActioning: boolean;
  onAttend: (id: string, date: string) => void;
  onComplete: (b: ClinicBooking) => void;
  onRate: (b: ClinicBooking) => void;
  onCert: (b: ClinicBooking) => void;
}) {
  const days = b.attendanceDays ?? [];
  const hasDayActions = days.some(d => !d.attended);
  const hasPrimary =
    b.canMarkComplete || b.canEvaluate || b.canIssueCertificate;

  if (b.hasEvaluation && !b.canIssueCertificate) {
    return (
      <div className="cd-row-actions cd-row-actions--done">
        <div className="cd-done-chip">Week complete</div>
      </div>
    );
  }

  if (!hasDayActions && !hasPrimary && !b.hasEvaluation) {
    return (
      <div className="cd-row-actions">
        <span className="cd-idle-hint">Nothing to do yet</span>
      </div>
    );
  }

  return (
    <div className="cd-row-actions">
      {days.length > 0 && (
        <div className="cd-att-block">
          <div className="cd-att-label">Days</div>
          <div className="cd-att-chips">
            {days.map(day => (
              day.attended ? (
                <span key={day.date} className="cd-att-chip cd-att-chip--done" title="Attended">
                  {formatAttendDay(day.date)} ✓
                </span>
              ) : (
                <button
                  key={day.date}
                  type="button"
                  className="cd-att-chip cd-att-chip--todo"
                  disabled={!day.canMark || isActioning}
                  onClick={() => onAttend(b.id, day.date)}
                  title={day.canMark ? 'Mark attendance' : 'Not available yet'}
                >
                  {formatAttendDay(day.date)}
                </button>
              )
            ))}
          </div>
        </div>
      )}

      {(hasPrimary || b.canIssueCertificate) && (
        <div className="cd-cta-row">
          {b.canMarkComplete && (
            <button
              type="button"
              className="cd-cta cd-cta--secondary"
              disabled={isActioning}
              onClick={() => onComplete(b)}
            >
              Complete
            </button>
          )}
          {b.canEvaluate && (
            <button
              type="button"
              className="cd-cta cd-cta--primary"
              disabled={isActioning}
              onClick={() => onRate(b)}
            >
              Rate
            </button>
          )}
          {b.canIssueCertificate && (
            <button
              type="button"
              className="cd-cta cd-cta--cert"
              disabled={isActioning}
              onClick={() => onCert(b)}
            >
              Certificate
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function formatNationalIdTail(nid?: string) {
  if (!nid) return '—';
  const digits = nid.replace(/\D/g, '');
  if (digits.length === 0) return '—';
  return digits.slice(-5);
}

function InternCard({
  b,
  onAttend,
  onComplete,
  onRate,
  onCert,
  isActioning,
}: {
  b: ClinicBooking;
  isActioning: boolean;
  onAttend: (id: string, date: string) => void;
  onComplete: (b: ClinicBooking) => void;
  onRate: (b: ClinicBooking) => void;
  onCert: (b: ClinicBooking) => void;
}) {
  const status = internStatus(b);
  const prog = weekProgress(b);
  return (
    <article className="cd-intern-card">
      <div className="cd-intern-card-top">
        <div className="cd-student-cell">
          <StudentPhoto photoUrl={b.studentPhotoUrl} />
          <div>
            <div className="s-name">{b.studentName || 'Student Intern'}</div>
            <div className="nid">NID · {formatNationalIdTail(b.studentNationalId)}</div>
          </div>
        </div>
        <span className={`status-pill ${statusPillClass(status.tone)}`}>{status.label}</span>
      </div>
      <div className="cd-intern-card-meta">
        <span className="br-badge">{b.locationName || '—'}</span>
        <span className="sched">{formatSchedule(b)}</span>
      </div>
      <div className="progress-cell">
        <div className="progress-meta">
          <span className="progress-text">{prog.total ? `${prog.done} / ${prog.total} days` : '—'}</span>
          <span className="progress-pct">{prog.pct}%</span>
        </div>
        <div className="mini-progress"><span style={{ width: `${prog.pct}%` }} /></div>
      </div>
      <InternRowActions
        b={b}
        isActioning={isActioning}
        onAttend={onAttend}
        onComplete={onComplete}
        onRate={onRate}
        onCert={onCert}
      />
    </article>
  );
}

function usePhoneLayout(maxWidth = 900) {
  const [isPhone, setIsPhone] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${maxWidth}px)`).matches
  ));
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = () => setIsPhone(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [maxWidth]);
  return isPhone;
}

export function ClinicDashboard() {
  const { me, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const user = me ? { name: me.fullName, email: me.email } : null;
  const isClinic = me?.role === 'clinic_staff';
  const queryClient = useQueryClient();
  const isPhone = usePhoneLayout();
  const [phoneTab, setPhoneTab] = useState<'chats' | 'clinic'>('chats');
  const [phoneMenuOpen, setPhoneMenuOpen] = useState(false);

  // Active view tab state
  const [activeNav, setActiveNav] = useState<'dashboard' | 'analytics'>('dashboard');
  const [branchesExpanded, setBranchesExpanded] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDropOpen, setFilterDropOpen] = useState(false);
  const [selectedBranchFilters, setSelectedBranchFilters] = useState<string[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);

  // Modals
  const [ratingBooking, setRatingBooking] = useState<ClinicBooking | null>(null);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [exploreSearch, setExploreSearch] = useState('');
  const [exploreDetailToken, setExploreDetailToken] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSnapshotModal, setActiveSnapshotModal] = useState<'active' | 'thisWeek' | 'pending' | 'messages' | null>(null);

  // Chat panel state
  const [chatActiveKey, setChatActiveKey] = useState<string | null>(null);
  const [activeDoctorId, setActiveDoctorId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('physioedge.clinic.activeDoctorId');
    } catch {
      return null;
    }
  });
  const [submittingAnswer, setSubmittingAnswer] = useState(false);

  // Actions loading state
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [welcomeOpen, setWelcomeOpen] = useState(() => {
    const fromOnboarding = Boolean(
      (location.state as { clinicWelcome?: boolean } | null)?.clinicWelcome,
    );
    return fromOnboarding || Boolean(me && clinicWelcomeIsPending(me.id));
  });

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  useEffect(() => {
    if (!me) return;
    const fromOnboarding = Boolean(
      (location.state as { clinicWelcome?: boolean } | null)?.clinicWelcome,
    );
    if (fromOnboarding || clinicWelcomeIsPending(me.id)) setWelcomeOpen(true);
  }, [me, location.state]);

  // Queries
  const clinicMeQuery = useQuery({
    queryKey: ['clinic-me'],
    queryFn: () => api.getClinicMe(),
    enabled: isClinic,
    staleTime: 60000,
  });
  const isPrimary = clinicMeQuery.data?.isPrimary !== false;
  const isBranchStaff = !isPrimary;

  useEffect(() => {
    if (!isPrimary && phoneTab === 'chats') setPhoneTab('clinic');
  }, [isPrimary, phoneTab]);

  const statsQuery = useQuery<ClinicStats>({
    queryKey: ['clinic-stats'],
    queryFn: () => api.getClinicStats(),
    enabled: isClinic,
    staleTime: 30000,
  });

  const bookingsQuery = useQuery({
    queryKey: ['clinic-bookings'],
    queryFn: () => api.getClinicBookings(),
    enabled: isClinic,
    staleTime: 15000,
  });

  const withdrawalsQuery = useQuery({
    queryKey: ['clinic-withdrawals'],
    queryFn: () => api.getClinicWithdrawals(),
    enabled: isClinic && isPrimary && (withdrawalOpen || historyOpen),
    staleTime: 30000,
  });

  const withdrawalHistory = useMemo(() => {
    const list = Array.isArray(withdrawalsQuery.data) ? withdrawalsQuery.data : [];
    return [...list].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [withdrawalsQuery.data]);

  const consultationsQuery = useQuery({
    queryKey: ['clinic-consultations'],
    queryFn: () => api.getClinicConsultations(),
    enabled: isClinic && isPrimary,
    staleTime: 15000,
  });

  const exploreStudentsQuery = useQuery({
    queryKey: ['clinic-explore-students'],
    queryFn: () => api.getExploreStudents(),
    enabled: isClinic && isPrimary && exploreOpen,
    staleTime: 30000,
  });

  const exploreDetailQuery = useQuery({
    queryKey: ['clinic-explore-student', exploreDetailToken],
    queryFn: () => api.getExploreStudentDetail(exploreDetailToken!),
    enabled: isClinic && isPrimary && Boolean(exploreDetailToken),
  });

  const stats = statsQuery.data;
  const bookings = Array.isArray(bookingsQuery.data) ? bookingsQuery.data : [];
  const locations = clinicMeQuery.data?.locations ?? [];
  const consultations: ClinicConsultation[] = consultationsQuery.data?.consultations ?? [];
  const clinicDoctors = useMemo(() => {
    const docs = consultationsQuery.data?.doctors
      ?? (consultationsQuery.data?.doctor ? [consultationsQuery.data.doctor] : []);
    return docs
      .filter(d => Boolean(d?.id && d?.fullName))
      .map(d => ({
        id: d.id,
        fullName: d.fullName,
        specialty: d.specialty ?? null,
        photoUrl: d.photoUrl ?? null,
        acceptingConsultations: d.acceptingConsultations,
        isActive: d.isActive,
      }));
  }, [consultationsQuery.data?.doctors, consultationsQuery.data?.doctor]);
  const clinic = clinicMeQuery.data?.clinic;

  useEffect(() => {
    if (clinicDoctors.length === 0) return;
    if (activeDoctorId && clinicDoctors.some(d => d.id === activeDoctorId)) return;
    const next = clinicDoctors[0]!.id;
    setActiveDoctorId(next);
    try {
      localStorage.setItem('physioedge.clinic.activeDoctorId', next);
    } catch {
      // ignore
    }
  }, [clinicDoctors, activeDoctorId]);

  const selectDoctor = (id: string) => {
    setActiveDoctorId(id);
    setChatActiveKey(null);
    try {
      localStorage.setItem('physioedge.clinic.activeDoctorId', id);
    } catch {
      // ignore
    }
  };

  const openClinicView = (nav: 'dashboard' | 'analytics' = 'dashboard') => {
    setActiveNav(nav);
    setPhoneTab('clinic');
    setPhoneMenuOpen(false);
    if (isPhone) setSidebarCollapsed(false);
  };

  // Available branches list for filter
  const branchNames = useMemo(() => {
    const set = new Set<string>();
    locations.forEach(l => { if (l.name) set.add(l.name); });
    bookings.forEach(b => { if (b.locationName) set.add(b.locationName); });
    return Array.from(set).sort();
  }, [locations, bookings]);

  // Sync selected branch checkboxes when branch list loads
  useEffect(() => {
    if (branchNames.length > 0 && selectedBranchFilters.length === 0) {
      setSelectedBranchFilters(branchNames);
    }
  }, [branchNames, selectedBranchFilters.length]);

  // Filtered interns list
  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      if (b.status === 'cancelled') return false;
      if (selectedBranch && b.locationName !== selectedBranch) return false;
      if (
        selectedBranchFilters.length > 0
        && selectedBranchFilters.length < branchNames.length
        && b.locationName
        && !selectedBranchFilters.includes(b.locationName)
      ) {
        return false;
      }
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = b.studentName?.toLowerCase().includes(q);
        const matchNid = b.studentNationalId?.toLowerCase().includes(q);
        const matchBranch = b.locationName?.toLowerCase().includes(q);
        if (!matchName && !matchNid && !matchBranch) return false;
      }
      return true;
    });
  }, [bookings, selectedBranch, selectedBranchFilters, searchTerm, branchNames.length]);

  const internRows = useMemo(() => {
    const rank = (b: ClinicBooking) => {
      if (b.canEvaluate) return 0;
      if (phaseOf(b) === 'in_progress') return 1;
      if (phaseOf(b) === 'upcoming') return 2;
      if (b.hasEvaluation) return 3;
      return 4;
    };
    return [...filteredBookings].sort((a, b) => rank(a) - rank(b));
  }, [filteredBookings]);

  const thisWeekBookings = useMemo(() => bookings.filter(overlapsThisWeek), [bookings]);
  const pendingRatings = useMemo(() => bookings.filter(b => b.canEvaluate), [bookings]);
  const unreadMessages = useMemo(
    () => consultations.filter(c => c.status === 'pending'),
    [consultations],
  );

  const handleConfirmAttendance = async (bookingId: string, sessionDate: string) => {
    try {
      setActionLoadingId(bookingId);
      await api.confirmAttendance(bookingId, sessionDate);
      showToast(`Attendance confirmed for ${sessionDate}.`);
      await queryClient.invalidateQueries({ queryKey: ['clinic-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['clinic-stats'] });
    } catch (err) {
      showToast(err instanceof ApiError ? err.friendlyMessage : 'Failed to confirm attendance');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCompleteBooking = async (booking: ClinicBooking) => {
    try {
      setActionLoadingId(booking.id);
      await api.completeBooking(booking.id);
      showToast(`Marked week complete for ${booking.studentName || 'intern'}.`);
      await queryClient.invalidateQueries({ queryKey: ['clinic-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['clinic-stats'] });
    } catch (err) {
      showToast(err instanceof ApiError ? err.friendlyMessage : 'Failed to mark complete');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRatingSubmit = async (scores: EvalScores, comment?: string) => {
    if (!ratingBooking) return;
    try {
      setSubmittingRating(true);
      await api.evaluateBooking(ratingBooking.id, {
        professionalism: scores.professionalism,
        ethicalCode: scores.ethicalCode,
        timeRespect: scores.timeRespect,
        communication: scores.communication,
        ...(comment ? { comment } : {}),
      });
      showToast(`Evaluation submitted for ${ratingBooking.studentName || 'intern'}!`);
      setRatingBooking(null);
      await queryClient.invalidateQueries({ queryKey: ['clinic-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['clinic-stats'] });
    } catch (err) {
      showToast(err instanceof ApiError ? err.friendlyMessage : 'Failed to submit evaluation');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleIssueCertificate = async (booking: ClinicBooking) => {
    try {
      setActionLoadingId(booking.id);
      await api.issueCertificate(booking.id);
      showToast(`Certificate generated for ${booking.studentName || 'intern'}!`);
      await queryClient.invalidateQueries({ queryKey: ['clinic-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['clinic-stats'] });
    } catch (err) {
      showToast(err instanceof ApiError ? err.friendlyMessage : 'Failed to issue certificate');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleAnswerSubmit = async (consultationId: string, answer: string) => {
    if (!answer.trim()) return;
    try {
      setSubmittingAnswer(true);
      await api.answerConsultation(consultationId, answer.trim());
      showToast('Reply sent to student.');
      await queryClient.invalidateQueries({ queryKey: ['clinic-consultations'] });
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Failed to send reply');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleWithdrawalSubmit = async () => {
    if (!stats || stats.availableEgp <= 0) {
      showToast('No available balance to withdraw.');
      return;
    }
    try {
      setSubmittingWithdrawal(true);
      const res = await api.requestWithdrawal();
      showToast(`Full wallet withdrawal requested for ${egp(res.amountEgp)}.`);
      setWithdrawalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['clinic-stats'] });
      await queryClient.invalidateQueries({ queryKey: ['clinic-withdrawals'] });
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Failed to submit withdrawal request');
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const exploreStudents = useMemo(() => {
    const list = exploreStudentsQuery.data?.students ?? [];
    const q = exploreSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter(s => s.displayName.toLowerCase().includes(q));
  }, [exploreStudentsQuery.data?.students, exploreSearch]);

  const finishWelcome = () => {
    if (me) dismissClinicWelcome(me.id);
    setWelcomeOpen(false);
    if ((location.state as { clinicWelcome?: boolean } | null)?.clinicWelcome) {
      navigate('/clinic', { replace: true, state: {} });
    }
  };

  if (statsQuery.isLoading || bookingsQuery.isLoading || clinicMeQuery.isLoading) {
    return (
      <div className="clinic-dash" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--cd-g500)', fontSize: 13 }}>Loading Clinic Dashboard…</div>
      </div>
    );
  }

  const activeCount = stats?.uniqueInProgressStudents ?? bookings.filter(b => phaseOf(b) === 'in_progress').length;
  const thisWeekCount = stats?.thisWeekCount ?? thisWeekBookings.length;
  const pendingRatingsCount = stats?.evaluationsPending ?? pendingRatings.length;
  const unreadMessagesCount = unreadMessages.length;
  const firstName = (me?.fullName ?? '').trim().split(/\s+/)[0] ?? '';
  const completedWeeks = stats?.completedWeeks ?? 0;
  const payoutRate = stats?.payoutRatePerWeekEgp ?? 0;
  const internWeeks = stats?.internWeeks ?? 0;
  const capacityTargetEgp = stats?.totalRevenueEgp ?? internWeeks * payoutRate;
  const earnedEgp = stats?.earnedEgp ?? 0;
  const capacityPct = capacityTargetEgp > 0
    ? Math.min(100, Math.round((earnedEgp / capacityTargetEgp) * 100))
    : 0;
  const formatEgpAmount = (n: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const formatWeeks = (n: number) => n.toLocaleString('en-US');

  return (
    <div
      className={[
        'clinic-dash',
        sidebarCollapsed && !isPhone ? 'clinic-dash--sb-collapsed' : '',
        !isPrimary ? 'clinic-dash--no-chat' : '',
        isPhone ? 'clinic-dash--phone' : '',
        isPhone ? `clinic-dash--phone-${isPrimary ? phoneTab : 'clinic'}` : '',
        isPhone && phoneMenuOpen ? 'clinic-dash--phone-menu' : '',
      ].filter(Boolean).join(' ')}
    >
      {welcomeOpen && (
        <ClinicWelcomeTour userId={me?.id} firstName={firstName} onDone={finishWelcome} />
      )}

      {isPhone && phoneMenuOpen && (
        <button
          type="button"
          className="cd-phone-scrim"
          aria-label="Close menu"
          onClick={() => setPhoneMenuOpen(false)}
        />
      )}

      {/* Collapsed left rail */}
      {sidebarCollapsed && !isPhone && (
        <button
          type="button"
          className="cd-rail cd-rail--left"
          onClick={() => setSidebarCollapsed(false)}
          aria-label="Open menu sidebar"
          title="Open menu"
        >
          <PanelLeft className="cd-rail-ico" />
          <span className="cd-rail-label">Menu</span>
          <ChevronRight className="cd-rail-chev" />
        </button>
      )}

      {/* SIDEBAR */}
      <aside className={`cd-sidebar${sidebarCollapsed && !isPhone ? ' is-collapsed' : ''}`} aria-hidden={isPhone ? !phoneMenuOpen : sidebarCollapsed}>
        <div className="cd-sb-logo">
          <div className="cd-sb-lbl" style={{ margin: 0, flex: 1 }}>Menu</div>
          {!isPhone && (
          <button
            type="button"
            className="cd-panel-toggle"
            onClick={() => setSidebarCollapsed(true)}
            aria-label="Collapse menu"
            title="Collapse menu"
          >
            <ChevronLeft size={16} />
          </button>
          )}
          {isPhone && (
          <button
            type="button"
            className="cd-panel-toggle"
            onClick={() => setPhoneMenuOpen(false)}
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
          )}
        </div>

        <div className="cd-sb-section">
          <button
            type="button"
            className={`cd-nav-item${activeNav === 'dashboard' && !selectedBranch ? ' active' : ''}`}
            onClick={() => {
              setSelectedBranch(null);
              openClinicView('dashboard');
            }}
          >
            <LayoutDashboard className="cd-ico" />
            Dashboard
          </button>

          <button
            type="button"
            className={`cd-nav-item${selectedBranch ? ' active' : ''}`}
            onClick={() => setBranchesExpanded(!branchesExpanded)}
          >
            <GitBranch className="cd-ico" />
            Branches
            <ChevronDown
              className="cd-chev"
              style={{ transform: branchesExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </button>

          {branchesExpanded && (
            <div className="cd-branch-list">
              <button
                type="button"
                className={`cd-branch-item${!selectedBranch ? ' active' : ''}`}
                onClick={() => {
                  setSelectedBranch(null);
                  openClinicView('dashboard');
                }}
              >
                <div className="cd-branch-dot" /> All Branches
              </button>
              {locations.map((loc: { id: string; name: string }) => (
                <button
                  key={loc.id}
                  type="button"
                  className={`cd-branch-item${selectedBranch === loc.name ? ' active' : ''}`}
                  onClick={() => {
                    setSelectedBranch(loc.name);
                    openClinicView('dashboard');
                  }}
                >
                  <div className="cd-branch-dot" /> {loc.name}
                </button>
              ))}
            </div>
          )}

          {isPrimary && (
            <button
              type="button"
              className={`cd-nav-item${activeNav === 'analytics' ? ' active' : ''}`}
              onClick={() => {
                setSelectedBranch(null);
                openClinicView('analytics');
              }}
            >
              <BarChart3 className="cd-ico" />
              Analytics
            </button>
          )}

          {isPrimary && (
            <button
              type="button"
              className="cd-nav-item"
              onClick={() => {
                setPhoneMenuOpen(false);
                navigate('/clinic/management');
              }}
            >
              <UserCog className="cd-ico" />
              Account
            </button>
          )}
        </div>

        <div className="cd-sb-footer">
          <div className="cd-clinic-row">
            <div className="cd-clinic-av">
              {clinic?.logoUrl ? (
                <img src={clinic.logoUrl} alt={clinic.name || 'Clinic logo'} />
              ) : (
                (clinic?.name ?? user?.name ?? 'PE').slice(0, 2).toUpperCase()
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="cd-clinic-name">{clinic?.name || user?.name || 'Clinic Account'}</div>
              <div className="cd-clinic-role">
                {isBranchStaff ? 'Branch Staff' : 'Clinic Admin'} · {locations.length} branch{locations.length === 1 ? '' : 'es'}
              </div>
            </div>
          </div>
          <button type="button" className="cd-signout" onClick={logout}>
            Sign Out
          </button>
        </div>
      </aside>


      {/* MAIN CONTENT AREA */}
      <div className="cd-main">
        <div className="cd-topbar">
          <div className="cd-topbar-l">
            <div className="cd-topbar-brand" aria-label="PhysioEDGE Club">
              <BrandMark size="sm" />
              <div className="cd-topbar-brand-text">
                <span className="l1">Physio</span>
                <span className="l2">EDGE</span>
                <span className="l3">Club</span>
              </div>
            </div>
            <div className="cd-topbar-titles">
              <div className="cd-pg-title">
                {activeNav === 'analytics'
                  ? 'Analytics & Branch Performance'
                  : selectedBranch
                  ? `Branch: ${selectedBranch}`
                  : 'Dashboard — All Branches'}
                {isPrimary && clinic?.bookingEnabled === false && (
                  <span className="cd-visibility-pill">Hidden from booking</span>
                )}
              </div>
              <div className="cd-pg-sub">
                {new Date().toLocaleDateString('en-GB', {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
            </div>
          </div>

          <div className="cd-topbar-r">
            <div className="cd-notif-wrap">
              <button
                type="button"
                className={`cd-notif-btn${notifOpen ? ' open' : ''}`}
                onClick={() => setNotifOpen(!notifOpen)}
                aria-label="Notifications"
              >
                <Bell className="cd-ico" />
                {pendingRatingsCount + (isPrimary ? unreadMessagesCount : 0) > 0 && (
                  <span className="cd-notif-badge">
                    {pendingRatingsCount + (isPrimary ? unreadMessagesCount : 0)}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="cd-notif-drop open">
                  <div className="cd-notif-hd">Notifications</div>
                  {pendingRatingsCount > 0 && (
                    <div className="cd-notif-item">
                      <strong>{pendingRatingsCount} rating{pendingRatingsCount === 1 ? '' : 's'} pending</strong>
                      <small>Review intern progress to release wallet funds.</small>
                    </div>
                  )}
                  {isPrimary && unreadMessagesCount > 0 && (
                    <div className="cd-notif-item">
                      <strong>{unreadMessagesCount} unread student message{unreadMessagesCount === 1 ? '' : 's'}</strong>
                      <small>Students are awaiting answers in consultations.</small>
                    </div>
                  )}
                  {pendingRatingsCount === 0 && (!isPrimary || unreadMessagesCount === 0) && (
                    <div className="cd-notif-item" style={{ color: 'var(--cd-g400)' }}>
                      All caught up! No pending notifications.
                    </div>
                  )}
                </div>
              )}
            </div>

            {isPrimary && (
              <button type="button" className="cd-ghost-btn" onClick={() => setExploreOpen(true)}>
                <Users className="cd-ico" /> Explore Students
              </button>
            )}

            {isPrimary && (
              <button type="button" className="cd-wd-btn" onClick={() => setWithdrawalOpen(true)}>
                <ArrowDownToLine className="cd-ico" /> Request Withdrawal
              </button>
            )}
          </div>
        </div>

        <div className="cd-body">
          {activeNav === 'analytics' && isPrimary ? (
            <ClinicAnalyticsPanel
              data={stats || {
                earnedEgp: 0, projectedEgp: 0, completedWeeks: 0, activeWeeks: 0,
                internWeeks: 0, totalRevenueEgp: 0,
                certificatesIssued: 0, earnedThisMonthEgp: 0, studentsThisMonth: 0,
                reservationsGrowthPct: null,
              }}
            />
          ) : (
            <>
              {isPrimary && (
                <div className="cd-wallet">
                  <button
                    type="button"
                    className="cd-wallet-balance-btn"
                    onClick={() => setHistoryOpen(true)}
                    title="View payment history"
                  >
                    <div className="cd-w-eyebrow">Available Balance</div>
                    <div className="cd-w-bal">
                      <span>EGP</span>
                      {egp(stats?.availableEgp ?? 0).replace(/^EGP\s*/i, '').replace(/\s*EGP$/i, '').trim()}
                    </div>
                    <div className="cd-w-sub">{egp(earnedEgp)} earned</div>
                  </button>
                  <div className="cd-w-div" />
                  <div>
                    <div className="cd-w-eyebrow">Intern weeks</div>
                    <div className="cd-w-stat-val">{formatWeeks(completedWeeks)} / {formatWeeks(internWeeks)}</div>
                  </div>
                  <div className="cd-w-div" />
                  <div className="cd-w-prog-wrap">
                    <div className="cd-w-prog-hd">
                      <span>Full capacity</span>
                      <strong>
                        {formatEgpAmount(earnedEgp)} / {formatEgpAmount(capacityTargetEgp)} EGP
                      </strong>
                    </div>
                    <div className="cd-w-bar-bg">
                      <div className="cd-w-bar-fill" style={{ width: `${capacityPct}%` }} />
                    </div>
                    {payoutRate > 0 && (
                      <div className="cd-w-prog-sub">{egp(payoutRate)}/week · as agreed in partner terms</div>
                    )}
                  </div>
                </div>
              )}

              <div className={`cd-stats${!isPrimary ? ' cd-stats-3' : ''}`}>
                <button type="button" className="cd-stat-card" onClick={() => setActiveSnapshotModal('active')}>
                  <div className="cd-sc-top">
                    <div className="cd-sc-label">Active Interns</div>
                    <div className="cd-sc-icon cd-ic-navy"><Users className="cd-ico" /></div>
                  </div>
                  <div className="cd-sc-val">{activeCount}</div>
                </button>
                <button type="button" className="cd-stat-card" onClick={() => setActiveSnapshotModal('thisWeek')}>
                  <div className="cd-sc-top">
                    <div className="cd-sc-label">This Week</div>
                    <div className="cd-sc-icon cd-ic-orange"><Calendar className="cd-ico" /></div>
                  </div>
                  <div className="cd-sc-val">{thisWeekCount}</div>
                </button>
                <button type="button" className="cd-stat-card" onClick={() => setActiveSnapshotModal('pending')}>
                  <div className="cd-sc-top">
                    <div className="cd-sc-label">Pending Ratings</div>
                    <div className="cd-sc-icon cd-ic-orange"><Star className="cd-ico" /></div>
                  </div>
                  <div className="cd-sc-val">{pendingRatingsCount}</div>
                </button>
                {isPrimary && (
                  <button type="button" className="cd-stat-card" onClick={() => {
                    if (isPhone) {
                      setPhoneTab('chats');
                      return;
                    }
                    setActiveSnapshotModal('messages');
                  }}>
                    <div className="cd-sc-top">
                      <div className="cd-sc-label">Unread Messages</div>
                      <div className="cd-sc-icon cd-ic-navy"><MessageCircle className="cd-ico" /></div>
                    </div>
                    <div className="cd-sc-val">{unreadMessagesCount}</div>
                  </button>
                )}
              </div>

              <div className="cd-table-card">
                <div className="cd-table-hd">
                  <div>
                    <span className="cd-table-title">
                      {selectedBranch ? `${selectedBranch} Interns` : 'All Active Interns'}
                    </span>
                    <span className="cd-table-cnt">{internRows.length} student{internRows.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="cd-table-acts">
                    <div className="cd-search-wrap">
                      <Search className="cd-search-ico" />
                      <input
                        className="cd-search-input"
                        type="text"
                        placeholder="Search student…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                    </div>
                    {branchNames.length > 1 && (
                      <div className="cd-filter-wrap">
                        <button
                          type="button"
                          className={`cd-filter-btn${selectedBranchFilters.length < branchNames.length ? ' active' : ''}`}
                          onClick={() => setFilterDropOpen(!filterDropOpen)}
                        >
                          <Filter className="cd-ico" />
                          <span>
                            {selectedBranchFilters.length === branchNames.length
                              ? 'Filter by Branch'
                              : `${selectedBranchFilters.length} selected`}
                          </span>
                        </button>
                        {filterDropOpen && (
                          <div className="cd-f-dropdown open">
                            <div className="cd-f-lbl">Select Branches</div>
                            <label className="cd-f-opt">
                              <input
                                type="checkbox"
                                checked={selectedBranchFilters.length === branchNames.length}
                                onChange={e => setSelectedBranchFilters(e.target.checked ? branchNames : [])}
                              />
                              All Branches
                            </label>
                            {branchNames.map(bName => (
                              <label key={bName} className="cd-f-opt">
                                <input
                                  type="checkbox"
                                  checked={selectedBranchFilters.includes(bName)}
                                  onChange={e => {
                                    if (e.target.checked) setSelectedBranchFilters([...selectedBranchFilters, bName]);
                                    else setSelectedBranchFilters(selectedBranchFilters.filter(x => x !== bName));
                                  }}
                                />
                                {bName}
                              </label>
                            ))}
                            <div className="cd-f-actions">
                              <button type="button" className="cd-f-clear" onClick={() => setSelectedBranchFilters(branchNames)}>Reset</button>
                              <button type="button" className="cd-f-apply" onClick={() => setFilterDropOpen(false)}>Apply</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="cd-table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Student Name</th>
                        <th>NID (last 5)</th>
                        <th>Branch</th>
                        <th>Schedule</th>
                        <th>Progress</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {internRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: 'var(--cd-g400)' }}>
                            No interns match the selected filters.
                          </td>
                        </tr>
                      ) : (
                        internRows.map((b, idx) => {
                          const status = internStatus(b);
                          const isActioning = actionLoadingId === b.id;
                          const prog = weekProgress(b);
                          return (
                            <tr key={b.id}>
                              <td style={{ color: 'var(--cd-g400)', fontSize: '11.5px' }}>{String(idx + 1).padStart(2, '0')}</td>
                              <td>
                                <div className="cd-student-cell">
                                  <StudentPhoto photoUrl={b.studentPhotoUrl} />
                                  <div className="s-name">{b.studentName || 'Student Intern'}</div>
                                </div>
                              </td>
                              <td><div className="nid">{formatNationalIdTail(b.studentNationalId)}</div></td>
                              <td><span className="br-badge">{b.locationName || '—'}</span></td>
                              <td><span className="sched">{formatSchedule(b)}</span></td>
                              <td>
                                <div className="progress-cell">
                                  <div className="progress-meta">
                                    <span className="progress-text">{prog.total ? `${prog.done} / ${prog.total}` : '—'}</span>
                                    <span className="progress-pct">{prog.pct}%</span>
                                  </div>
                                  <div className="mini-progress"><span style={{ width: `${prog.pct}%` }} /></div>
                                </div>
                              </td>
                              <td>
                                <span className={`status-pill ${statusPillClass(status.tone)}`}>
                                  {status.label}
                                </span>
                              </td>
                              <td>
                                <InternRowActions
                                  b={b}
                                  isActioning={isActioning}
                                  onAttend={handleConfirmAttendance}
                                  onComplete={handleCompleteBooking}
                                  onRate={setRatingBooking}
                                  onCert={handleIssueCertificate}
                                />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="cd-intern-cards">
                  {internRows.length === 0 ? (
                    <div className="cd-intern-empty">No interns match the selected filters.</div>
                  ) : (
                    internRows.map(b => (
                      <InternCard
                        key={b.id}
                        b={b}
                        isActioning={actionLoadingId === b.id}
                        onAttend={handleConfirmAttendance}
                        onComplete={handleCompleteBooking}
                        onRate={setRatingBooking}
                        onCert={handleIssueCertificate}
                      />
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {isPrimary && (
        <ClinicChatPanel
          consultations={consultations}
          doctors={clinicDoctors}
          selectedDoctorId={activeDoctorId}
          onSelectDoctor={selectDoctor}
          loading={consultationsQuery.isLoading}
          activeKey={chatActiveKey}
          onSelect={setChatActiveKey}
          onSubmitAnswer={handleAnswerSubmit}
          submitting={submittingAnswer}
          stacked={isPhone}
        />
      )}

      {isPhone && (
        <nav className="cd-phone-nav" aria-label="Clinic mobile navigation">
          {isPrimary && (
            <button
              type="button"
              className={`cd-phone-tab${phoneTab === 'chats' ? ' active' : ''}`}
              onClick={() => { setPhoneTab('chats'); setPhoneMenuOpen(false); }}
            >
              <span className="cd-phone-tab-ico">
                <MessageCircle size={20} />
                {unreadMessagesCount > 0 && (
                  <span className="cd-phone-tab-badge">{unreadMessagesCount}</span>
                )}
              </span>
              Chats
            </button>
          )}
          <button
            type="button"
            className={`cd-phone-tab${phoneTab === 'clinic' && !phoneMenuOpen ? ' active' : ''}`}
            onClick={() => openClinicView(activeNav === 'analytics' ? 'analytics' : 'dashboard')}
          >
            <LayoutDashboard size={20} />
            Manage
          </button>
          <button
            type="button"
            className={`cd-phone-tab${phoneMenuOpen ? ' active' : ''}`}
            onClick={() => setPhoneMenuOpen(open => !open)}
            aria-expanded={phoneMenuOpen}
          >
            <Menu size={20} />
            Menu
          </button>
        </nav>
      )}

      <ClinicRatingModal
        studentName={ratingBooking?.studentName || 'Student Intern'}
        weekLabel={ratingBooking ? `${ratingBooking.weekStartDate} → ${ratingBooking.weekEndDate}` : undefined}
        open={!!ratingBooking}
        onClose={() => setRatingBooking(null)}
        onSubmit={handleRatingSubmit}
        submitting={submittingRating}
      />

      {withdrawalOpen && (
        <div className="cd-overlay open" onClick={e => { if (e.target === e.currentTarget) setWithdrawalOpen(false); }}>
          <div className="cd-modal cd-modal-lg withdraw-modal" role="dialog" aria-modal="true">
            <div className="cd-modal-hd">
              <div>
                <div className="cd-modal-title">Request Withdrawal</div>
              </div>
              <button type="button" className="cd-modal-x" onClick={() => setWithdrawalOpen(false)}><X size={17} /></button>
            </div>
            <div className="balance-box">
              <div className="label">Full amount to withdraw</div>
              <div className="value">{egp(stats?.availableEgp ?? 0)}</div>
            </div>
            {withdrawalHistory.length > 0 && (
              <div style={{ marginTop: 4, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--cd-g400)', marginBottom: 6 }}>Recent payouts</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 120, overflowY: 'auto' }}>
                  {withdrawalHistory.slice(0, 3).map(w => (
                    <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, padding: '4px 8px', background: 'var(--cd-g50)', borderRadius: 6 }}>
                      <span>
                        {egp(w.amountEgp)}
                        {w.itemCount > 0 ? ` · ${w.itemCount} week${w.itemCount === 1 ? '' : 's'}` : ''}
                      </span>
                      <span className={`status-pill ${w.status === 'paid' ? 'sp-active' : 'sp-pending'}`}>
                        {w.status === 'requested' ? 'Pending payout' : w.status === 'paid' ? 'Sent' : w.status === 'rejected' ? 'Declined' : w.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="cd-modal-ft">
              <button type="button" className="cd-btn-cancel" onClick={() => setWithdrawalOpen(false)}>Cancel</button>
              <button
                type="button"
                className="cd-btn-submit"
                disabled={submittingWithdrawal || !(stats && stats.availableEgp > 0)}
                onClick={handleWithdrawalSubmit}
              >
                {submittingWithdrawal ? 'Requesting…' : `Withdraw all ${egp(stats?.availableEgp ?? 0)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyOpen && isPrimary && (
        <div className="cd-overlay open" onClick={e => { if (e.target === e.currentTarget) setHistoryOpen(false); }}>
          <div className="cd-modal cd-modal-lg" style={{ maxWidth: 520 }} role="dialog" aria-modal="true" aria-labelledby="cd-history-title">
            <div className="cd-modal-hd">
              <div>
                <div className="cd-modal-title" id="cd-history-title">Payment history</div>
              </div>
              <button type="button" className="cd-modal-x" onClick={() => setHistoryOpen(false)} aria-label="Close">
                <X size={17} />
              </button>
            </div>

            <div className="cd-history-summary">
              <div className="cd-history-sum-item">
                <span className="lbl">Available</span>
                <span className="val">{egp(stats?.availableEgp ?? 0)}</span>
              </div>
              <div className="cd-history-sum-item">
                <span className="lbl">Total earned</span>
                <span className="val">{egp(stats?.earnedEgp ?? 0)}</span>
              </div>
              <div className="cd-history-sum-item">
                <span className="lbl">Pending payout</span>
                <span className="val">{egp(stats?.pendingWithdrawalEgp ?? 0)}</span>
              </div>
              <div className="cd-history-sum-item">
                <span className="lbl">Sent</span>
                <span className="val">{egp(stats?.paidOutEgp ?? 0)}</span>
              </div>
            </div>

            <div className="cd-history-list">
              {withdrawalsQuery.isLoading ? (
                <div className="cd-history-empty">Loading history…</div>
              ) : withdrawalHistory.length === 0 ? (
                <div className="cd-history-empty">No withdrawal requests yet.</div>
              ) : (
                withdrawalHistory.map(w => (
                  <div key={w.id} className="cd-history-row">
                    <div>
                      <div className="cd-history-amt">
                        {egp(w.amountEgp)}
                        {w.itemCount > 0 ? (
                          <span style={{ fontWeight: 500, color: 'var(--cd-g400)', marginLeft: 6 }}>
                            · {w.itemCount} week{w.itemCount === 1 ? '' : 's'}
                          </span>
                        ) : null}
                      </div>
                      <div className="cd-history-date">
                        {new Date(w.createdAt).toLocaleString('en-GB', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                        {w.status === 'paid' && w.paidAt
                          ? ` · Sent ${new Date(w.paidAt).toLocaleDateString('en-GB')}`
                          : ''}
                      </div>
                    </div>
                    <span className={`status-pill ${w.status === 'paid' ? 'sp-active' : w.status === 'rejected' ? 'sp-rated' : 'sp-pending'}`}>
                      {w.status === 'requested' ? 'Pending payout' : w.status === 'paid' ? 'Sent' : w.status === 'rejected' ? 'Declined' : w.status}
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="cd-modal-ft" style={{ marginTop: 16 }}>
              <button type="button" className="cd-btn-cancel" onClick={() => setHistoryOpen(false)}>Close</button>
              {(stats?.availableEgp ?? 0) > 0 && (
                <button
                  type="button"
                  className="cd-btn-submit"
                  onClick={() => {
                    setHistoryOpen(false);
                    setWithdrawalOpen(true);
                  }}
                >
                  Request Withdrawal
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {exploreOpen && isPrimary && (
        <div
          className="cd-overlay open"
          onClick={e => {
            if (e.target === e.currentTarget) {
              setExploreOpen(false);
              setExploreSearch('');
              setExploreDetailToken(null);
            }
          }}
        >
          <div className="cd-modal cd-modal-xl" role="dialog" aria-modal="true" aria-labelledby="cd-explore-title">
            <div className="cd-modal-hd">
              <div>
                <div className="cd-modal-title" id="cd-explore-title">PhysioEDGE students</div>
              </div>
              <button
                type="button"
                className="cd-modal-x"
                onClick={() => { setExploreOpen(false); setExploreSearch(''); setExploreDetailToken(null); }}
                aria-label="Close"
              >
                <X size={17} />
              </button>
            </div>

            <div className="cd-explore-toolbar">
              <div className="cd-search-wrap" style={{ flex: 1 }}>
                <Search className="cd-search-ico" />
                <input
                  className="cd-search-input"
                  style={{ width: '100%' }}
                  type="text"
                  placeholder="Search by name…"
                  value={exploreSearch}
                  onChange={e => setExploreSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="cd-explore-count">
                {exploreStudentsQuery.isLoading
                  ? 'Loading…'
                  : `${exploreStudents.length} student${exploreStudents.length === 1 ? '' : 's'}`}
              </div>
            </div>

            <div className="cd-explore-table-wrap">
              {exploreStudentsQuery.isLoading ? (
                <div className="cd-history-empty">Loading students…</div>
              ) : exploreStudents.length === 0 ? (
                <div className="cd-history-empty">
                  {exploreSearch.trim() ? 'No students match that search.' : 'No approved students yet.'}
                </div>
              ) : (
                <table className="cd-explore-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student</th>
                      <th>PhysioEDGE</th>
                      <th>Weeks</th>
                      <th>Attendance</th>
                      <th>Certificates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exploreStudents.map((s, i) => (
                      <tr
                        key={s.exploreToken}
                        className="cd-explore-row"
                        onClick={() => setExploreDetailToken(s.exploreToken)}
                      >
                        <td className="cd-explore-num">{String(i + 1).padStart(2, '0')}</td>
                        <td>
                          <div className="cd-explore-student">
                            <StudentPhoto photoUrl={s.photoUrl} size="sm" blurred />
                            <div className="cd-explore-name">{s.displayName}</div>
                          </div>
                        </td>
                        <td>
                          {s.physioEdgeRating == null ? (
                            <span className="cd-explore-muted">New</span>
                          ) : (
                            <span className="cd-explore-rating">
                              <Star size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
                              {s.physioEdgeRating.toFixed(1)}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={s.weeksTaken > 0 ? 'cd-explore-metric' : 'cd-explore-muted'}>
                            {s.weeksTaken}
                          </span>
                        </td>
                        <td>
                          {s.attendancePercent == null ? (
                            <span className="cd-explore-muted">—</span>
                          ) : (
                            <span className="cd-explore-metric">{s.attendancePercent}%</span>
                          )}
                        </td>
                        <td>
                          <span className={s.certificateCount > 0 ? 'cd-explore-metric' : 'cd-explore-muted'}>
                            {s.certificateCount}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {exploreDetailToken && isPrimary && (
        <div className="cd-overlay open" onClick={e => { if (e.target === e.currentTarget) setExploreDetailToken(null); }}>
          <div className="cd-modal cd-modal-lg" role="dialog" aria-modal="true" aria-labelledby="cd-explore-detail-title">
            <div className="cd-modal-hd">
              <div>
                <div className="cd-modal-title" id="cd-explore-detail-title">Student profile</div>
              </div>
              <button type="button" className="cd-modal-x" onClick={() => setExploreDetailToken(null)} aria-label="Close">
                <X size={17} />
              </button>
            </div>

            {exploreDetailQuery.isLoading ? (
              <div className="cd-history-empty">Loading profile…</div>
            ) : exploreDetailQuery.isError || !exploreDetailQuery.data?.student ? (
              <div className="cd-history-empty">Could not load this student.</div>
            ) : (() => {
              const d = exploreDetailQuery.data.student;
              return (
                <div className="cd-explore-detail">
                  <div className="cd-explore-detail-hero">
                    <StudentPhoto photoUrl={d.photoUrl} blurred />
                    <div className="cd-explore-detail-hero-text">
                      <div className="cd-explore-name">{d.displayName}</div>
                      <div className="cd-explore-detail-score">
                        {d.physioEdgeRating == null ? (
                          <span className="cd-explore-muted">New</span>
                        ) : (
                          <>
                            <Star size={14} />
                            <strong>{d.physioEdgeRating.toFixed(1)}</strong>
                            <span>/ 5</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="cd-explore-stat-grid">
                    <div className="cd-explore-stat">
                      <Calendar size={14} />
                      <div>
                        <div className="cd-explore-stat-val">{d.weeksTaken}</div>
                        <div className="cd-explore-stat-lbl">Weeks taken</div>
                      </div>
                    </div>
                    <div className="cd-explore-stat">
                      <Percent size={14} />
                      <div>
                        <div className="cd-explore-stat-val">
                          {d.attendancePercent == null ? '—' : `${d.attendancePercent}%`}
                        </div>
                        <div className="cd-explore-stat-lbl">Attendance</div>
                      </div>
                    </div>
                    <div className="cd-explore-stat">
                      <Award size={14} />
                      <div>
                        <div className="cd-explore-stat-val">{d.certificateCount}</div>
                        <div className="cd-explore-stat-lbl">Certificates</div>
                      </div>
                    </div>
                    <div className="cd-explore-stat">
                      <Users size={14} />
                      <div>
                        <div className="cd-explore-stat-val">{d.cancelledWeeks}</div>
                        <div className="cd-explore-stat-lbl">Cancelled weeks</div>
                      </div>
                    </div>
                  </div>

                  <div className="cd-explore-section-title">Score breakdown</div>
                  <div className="cd-explore-breakdown">
                    {([
                      ['Clinic evaluations', d.ratingBreakdown.clinicEvaluations],
                      ['Reliability', d.ratingBreakdown.reliability],
                      ['Experience', d.ratingBreakdown.experience],
                      ['Attendance', d.ratingBreakdown.attendance],
                    ] as const).map(([label, value]) => (
                      <div key={label} className="cd-explore-breakdown-row">
                        <span>{label}</span>
                        <span>{value == null ? '—' : `${value.toFixed(1)} / 5`}</span>
                      </div>
                    ))}
                  </div>

                  {d.dimensionAverages && (
                    <>
                      <div className="cd-explore-section-title">Evaluation averages</div>
                      <div className="cd-explore-breakdown">
                        {([
                          ['Professionalism', d.dimensionAverages.professionalism],
                          ['Ethical code', d.dimensionAverages.ethicalCode],
                          ['Time respect', d.dimensionAverages.timeRespect],
                          ['Communication', d.dimensionAverages.communication],
                        ] as const).map(([label, value]) => (
                          <div key={label} className="cd-explore-breakdown-row">
                            <span>{label}</span>
                            <span>{value.toFixed(1)} / 5</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="cd-explore-section-title">
                    Prior placements
                    {d.evaluationCount > 0 ? ` (${d.evaluationCount})` : ''}
                  </div>
                  {d.recentEvaluations.length === 0 ? (
                    <div className="cd-explore-muted" style={{ padding: '4px 0 8px' }}>
                      No prior clinic evaluations yet.
                    </div>
                  ) : (
                    <div className="cd-explore-history">
                      {d.recentEvaluations.map((e, idx) => (
                        <div key={`${e.weekStartDate}-${idx}`} className="cd-explore-history-row">
                          <div>
                            <div className="cd-explore-history-title">{e.sourceLabel}</div>
                            <div className="cd-explore-muted">Week of {e.weekStartDate}</div>
                          </div>
                          <div className="cd-explore-rating">{e.average.toFixed(1)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {activeSnapshotModal && (
        <div className="cd-overlay open" onClick={e => { if (e.target === e.currentTarget) setActiveSnapshotModal(null); }}>
          <div className="cd-modal cd-modal-lg" role="dialog">
            <div className="cd-modal-hd">
              <div>
                <div className="cd-modal-title">
                  {activeSnapshotModal === 'active' ? 'Active Interns'
                    : activeSnapshotModal === 'thisWeek' ? 'This Week'
                    : activeSnapshotModal === 'pending' ? 'Pending Ratings'
                    : 'Unread Messages'}
                </div>
              </div>
              <button type="button" className="cd-modal-x" onClick={() => setActiveSnapshotModal(null)}><X size={17} /></button>
            </div>
            <div className="cd-snapshot-body">
              {activeSnapshotModal === 'messages' ? (
                unreadMessages.length === 0 ? (
                  <div className="cd-history-empty">No unread mentor questions.</div>
                ) : unreadMessages.map(c => (
                  <div key={c.id} className="cd-snapshot-row">
                    <div>
                      <div className="cd-snapshot-title">{c.studentName}</div>
                      <div className="cd-snapshot-sub">{c.question}</div>
                    </div>
                    <button
                      type="button"
                      className="cd-cta cd-cta--secondary"
                      onClick={() => {
                        setActiveSnapshotModal(null);
                        if (c.doctorId) selectDoctor(c.doctorId);
                        const student = c.studentId || c.studentName || c.id;
                        const doctor = c.doctorId || c.doctorName || 'doctor';
                        setChatActiveKey(`${student}::${doctor}`);
                      }}
                    >
                      Open chat
                    </button>
                  </div>
                ))
              ) : (() => {
                const rows = activeSnapshotModal === 'active'
                  ? bookings.filter(b => phaseOf(b) === 'in_progress')
                  : activeSnapshotModal === 'thisWeek'
                  ? thisWeekBookings
                  : pendingRatings;
                if (rows.length === 0) {
                  return <div className="cd-history-empty">Nothing to show.</div>;
                }
                return rows.map(b => (
                  <div key={b.id} className="cd-snapshot-row">
                    <div>
                      <div className="cd-snapshot-title">{b.studentName || 'Student'}</div>
                      <div className="cd-snapshot-sub">{b.locationName} · {formatBookingDays(b)}</div>
                    </div>
                    <div className="cd-snapshot-actions">
                      <span className={`status-pill ${statusPillClass(internStatus(b).tone)}`}>
                        {internStatus(b).label}
                      </span>
                      {b.canEvaluate && (
                        <button
                          type="button"
                          className="cd-cta cd-cta--primary"
                          onClick={() => {
                            setActiveSnapshotModal(null);
                            setRatingBooking(b);
                          }}
                        >
                          Rate
                        </button>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {toastMessage && <div className="toast show">{toastMessage}</div>}
    </div>
  );
}

export { ClinicDashboard as ClinicOverviewPage };
